import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import {
    MediaProgram,
    MediaProgramGuide,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import { ArrowForward, PlayArrowRounded } from '@mui/icons-material';
import { styleClinicalBadgeIcon } from '@rhg/components/program-card';
import { useFavoriteGuide } from '@rhg/hooks/use-favorite-guide';
import { useGuidePlayer } from '@rhg/hooks/use-guide-player';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { ProgramContextProps } from '@rhg/hooks/use-program-context/types';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import { useUnsponsoredDialog } from '@rhg/hooks/use-unsponsored-dialog';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import commonMessages from '@rhg/lang/messages/common';
import { Button, SxProps, Theme } from '@rhg/libs/material';
import {
    CalmClinicalBadge,
    LockFilled,
    PlayFilled,
    VideoFilled,
} from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { ProgramProvider } from '@rhg/providers/program';
import { MediaType, getMediaType } from '@rhg/utils/media';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Card, { CardProps } from '../card';
import { CardVariant } from '../card/types';
import { ChipLabel } from '../chip-label';
import { FavoriteButton } from '../favorite-button';
import StopPropagation from '../stop-propagation';

const focusIconStyle = {
    color: 'white',
    border: '2px solid white',
    borderRadius: '50%',
};

const styleFocusIconHorizontal = {
    ...focusIconStyle,
    width: 60,
    height: 60,
    p: 3,
};

const styleFocusIcon = {
    ...focusIconStyle,
    width: 80,
    height: 80,
    p: 4,
};

const styleHorizontalCard = { width: { md: 442, sm: '100%' } };

const styleChipLabel = {
    m: 4,
};
const styleLockedChipLabel = (isLocked?: boolean) => {
    if (!isLocked) {
        return undefined;
    }
    return {
        padding: 1,
        height: 'auto',
        width: 'auto',
        '.MuiChip-label': {
            padding: 0,
        },
        '.MuiChip-icon': {
            margin: 0,
        },
    };
};

const styleChipLabelHorizontal = { m: 2 };

const styleFavoriteButton = {
    p: 0,
    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
};

const styleFavoriteButtonBanner = { p: 1 };

const styleCarouselContent = { minHeight: 152, justifyContent: 'flex-start' };

const styleBottomChipIcon = { height: 18, width: 18 };

export interface SessionCardProps {
    program?: MediaProgram;
    packClass?: string;
    workflow?: WorkflowWorkflowInfo;
    variant: CardVariant;
    session: MediaProgramGuide;
    sessionIndex?: number;
    isStandalone?: boolean;
    willLockIfUnsponsored?: boolean;
    additionalAction?: React.ReactNode;
    categoryTitle?: string;
    sx?: SxProps;
    contentSx?: SxProps;
    skipSubtitle?: boolean;
    CardProps?: Partial<CardProps>;
}

type ContextAwareSessionCardProps = Omit<
    SessionCardProps,
    keyof ProgramContextProps
>;

export function SessionCard({
    program,
    workflow,
    packClass,
    ...contextAwareCardProps
}: SessionCardProps) {
    return (
        <ProgramProvider
            program={program}
            packClass={packClass}
            workflow={workflow}
        >
            <ContextAwareSessionCard {...contextAwareCardProps} />
        </ProgramProvider>
    );
}

function IconBottom({
    durationText,
    isLocked,
    isVideo,
    variant,
}: {
    durationText?: string;
    isLocked?: boolean;
    isVideo: boolean;
    variant: CardVariant;
}) {
    if (variant === 'banner') {
        return null;
    }

    if (!durationText) {
        return null;
    }
    const label = isLocked ? undefined : durationText;
    return (
        <ChipLabel
            sx={sxAppend(
                styleChipLabel,
                styleLockedChipLabel(isLocked),
                variant === 'horizontal' ? styleChipLabelHorizontal : undefined
            )}
            variant="darkTransparent"
            label={label}
            icon={
                isLocked ? (
                    <LockFilled />
                ) : isVideo ? (
                    <VideoFilled style={styleBottomChipIcon} />
                ) : (
                    <PlayFilled style={styleBottomChipIcon} />
                )
            }
        />
    );
}

export const ContextAwareSessionCard = ({
    variant,
    session,
    sessionIndex,
    isStandalone,
    additionalAction,
    willLockIfUnsponsored,
    categoryTitle: _categoryTitle,
    skipSubtitle = false,
    sx,
    contentSx,
    CardProps = {},
}: ContextAwareSessionCardProps) => {
    const { formatMessage } = useIntl();
    const { push } = useRouter();
    const { openGuidePlayer, onFavoriteSuccess } = useGuidePlayer();
    const {
        program,
        packClass,
        workflow,
        toolsByGuide,
        numSessions,
        programType,
        programState,
    } = useProvidedProgramContext();
    const { duration } = useHumanReadable();
    const packTitle = usePackContext();
    const { trackPackItemEvent } = useTrackSessionEvent();
    const { favoriteGuide, canFavorite } = useFavoriteGuide({
        onSuccess: onFavoriteSuccess,
        programId: program!.id,
        programType,
        packClass,
        programState,
    });

    const { userBlocker } = useUserBlocker();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const { showRegistrationDialog } = useRegistrationDialog();
    const isUnsponsored = userBlocker === UserBlocker.Unsponsored;

    const isLocked = useMemo(
        () => isUnsponsored && willLockIfUnsponsored,
        [isUnsponsored, willLockIfUnsponsored]
    );

    const isClinicalSession = useMemo(
        () => !!program?.isClinical,
        [program?.isClinical]
    );

    const playHandler = useCallback(async () => {
        if (program && packClass) {
            if (!isStandalone || isClinicalSession) {
                push(routes.programDetails(packClass, program.id));
            }
            if (isLocked) {
                if (await showUnsponsoredDialog()) {
                    showRegistrationDialog({
                        currentStep: RegistrationFlowStep.AccessCode,
                        registrationFlow: RegistrationFlow.ExistingRegistration,
                    });
                }
                return;
            } else {
                openGuidePlayer({
                    guide: session,
                    packClass,
                    workflow,
                    program,
                    tools: toolsByGuide[session.id],
                    willSkipToPreviousProgress: true,
                });
            }
        }
    }, [
        isClinicalSession,
        isLocked,
        isStandalone,
        openGuidePlayer,
        packClass,
        program,
        push,
        session,
        showUnsponsoredDialog,
        showRegistrationDialog,
        toolsByGuide,
        workflow,
    ]);

    const subtitle = useMemo(
        () =>
            !isStandalone || isClinicalSession
                ? program?.title
                : program?.narrator?.name,
        [
            isClinicalSession,
            isStandalone,
            program?.narrator?.name,
            program?.title,
        ]
    );

    const durationText = useMemo(() => {
        if (session?.meditationType === 'soundscape') {
            return;
        }
        return duration(session?.totalDuration);
    }, [session?.totalDuration, session.meditationType, duration]);

    const titleTopSpacing = useMemo(
        () => (variant === 'horizontal' ? 1 : undefined),
        [variant]
    );

    const categoryTitle = useMemo(() => {
        if (_categoryTitle) {
            return _categoryTitle;
        }
        return isClinicalSession
            ? formatMessage(
                  commonMessages['common.program_type.clinical_program.title']
              )
            : session.contentType;
    }, [_categoryTitle, isClinicalSession, session.contentType, formatMessage]);

    const categoryDescription = useMemo(() => {
        if (variant !== 'banner') {
            return;
        }

        if (isClinicalSession) {
            return formatMessage(commonMessages['common.sessions'], {
                sessions: numSessions,
            });
        }

        return durationText;
    }, [durationText, isClinicalSession, numSessions, formatMessage, variant]);

    const categoryIcon = useMemo(
        () =>
            isClinicalSession ? (
                <CalmClinicalBadge sx={styleClinicalBadgeIcon} />
            ) : undefined,
        [isClinicalSession]
    );

    const button = useMemo(
        () =>
            variant === 'banner' ? (
                <Button
                    trackButton="ProgramPlay"
                    startIcon={<PlayFilled />}
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={playHandler}
                >
                    {isClinicalSession
                        ? formatMessage(
                              commonMessages['common.play_session_number'],
                              {
                                  number: sessionIndex,
                              }
                          )
                        : formatMessage(commonMessages['common.play'])}
                </Button>
            ) : undefined,
        [isClinicalSession, playHandler, sessionIndex, formatMessage, variant]
    );

    const focusIcon = useMemo(() => {
        const sx =
            variant === 'horizontal'
                ? styleFocusIconHorizontal
                : styleFocusIcon;

        if (variant === 'banner') {
            return;
        }

        if (isStandalone) {
            return <PlayArrowRounded sx={sx} />;
        }

        return <ArrowForward sx={sx} />;
    }, [isStandalone, variant]);

    const imageForcedSize = useMemo(
        () =>
            variant === 'horizontal'
                ? DS_HEIGHT.HORIZONTAL_CARD_SIZE
                : undefined,
        [variant]
    );

    const horizontalSx = useMemo(
        () => (variant === 'horizontal' ? styleHorizontalCard : undefined),
        [variant]
    );

    const defaultContentSx = useMemo(
        () =>
            variant === 'carousel' || variant === 'carouselSquare'
                ? styleCarouselContent
                : undefined,
        [variant]
    );

    const isVideo = useMemo(
        () => getMediaType(session) === MediaType.Video,
        [session]
    );

    const overriddenAdditionalAction = useMemo(
        () =>
            additionalAction ?? (
                <StopPropagation>
                    <FavoriteButton
                        guide={session}
                        trackButtonFavorite="GuideItemFavorite"
                        trackButtonUnfavorite="GuideItemUnfavorite"
                        prefersDarkToast={false}
                        favoriteGuide={favoriteGuide}
                        canFavorite={canFavorite}
                        sx={sxAppend(
                            styleFavoriteButton,
                            variant === 'banner'
                                ? styleFavoriteButtonBanner
                                : undefined
                        )}
                    />
                </StopPropagation>
            ),
        [additionalAction, session, favoriteGuide, canFavorite, variant]
    );

    const onClick = useCallback(() => {
        if (variant !== 'banner') {
            playHandler();
        }
        trackPackItemEvent({
            eventName: EnumProductAnalyticsEvent.PackItemClicked,
            guide: session,
            program,
            packTitle,
        });
    }, [packTitle, playHandler, program, session, trackPackItemEvent, variant]);

    return (
        <Card
            title={session.title ?? ''}
            label={formatMessage(commonMessages['common.play'], {
                title: session?.title,
            })}
            titleMaxLines={2}
            imageSrc={session.image.url}
            imageAlt=""
            variant={variant}
            subtitle={!skipSubtitle ? subtitle : undefined}
            categoryTitle={categoryTitle}
            categoryTitleColor="text.sub"
            titleTopSpacing={titleTopSpacing}
            categoryIcon={categoryIcon}
            categoryDescription={categoryDescription}
            buttonGroup={button}
            imageFocusIcon={focusIcon}
            onClick={onClick}
            imageForcedSize={imageForcedSize}
            imageSideRadiusEnabled={false}
            sx={sxAppend(horizontalSx, sx)}
            contentSx={sxAppend(defaultContentSx, contentSx)}
            imageIconBottom={
                <IconBottom
                    durationText={durationText}
                    isLocked={isLocked}
                    isVideo={isVideo}
                    variant={variant}
                />
            }
            isEntireCardClickable={variant !== 'banner'}
            additionalAction={overriddenAdditionalAction}
            entireCardButtonProps={{
                ...CardProps.entireCardButtonProps,
                'data-cy': variant === 'banner' ? undefined : 'GuideCardPlay',
            }}
            data-syntheticid="SessionCard"
            {...CardProps}
        />
    );
};
